import {
	DialogBackdropState,
	renderReactDialog,
	Step,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import { HubItemCategoryKind } from "client/Api/Hub/hub";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { configureValidationStep, IObjectValidationStepBuilder } from "client/Validation/steps";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	HubCategorySavedResult,
	useEditHubCategoryDialog
} from "../../EditHubCategoryDialog/EditHubCategoryDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	KnowledgeItemSaveModel,
	EditKnowledgeItemPageViewModel,
	getNewKnowledgeItemModel,
	getEditKnowledgeItemModel,
	KnowledgeItemViewModel,
	KnowledgeItemStatus,
	createKnowledgeItem,
	updateKnowledgeItem,
	KnowledgeItemSaveRequestModel,
	checkPinLimit,
	CheckPinLimitResult
} from "client/Api/KnowledgeItemManagement/knowledgeItemManagement";
import {
	HubItemCategoryNodeKind,
	HubItemImageRestrictionsModel,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	ExistingHubItemImages,
	HubHierarchyListCategoryNodeModel,
	HubHierarchyListNodeModel,
	HubImageMediaKind
} from "client/Api/Hub/common.composer";
import { ContentStep, ContentStepHandle } from "./ContentStep";
import { SettingsStep } from "./SettingsStep";
import { WhoHasAccessStep } from "./WhoHasAccessStep";
import { IObjectValidatorBuilder, IValidationConfiguration } from "client/Validation/builder";
import { IObjectInputAttachable } from "client/Forms/attachable";
import {
	cleanUpMedia,
	convertLayoutToSaveModel,
	convertMediaToSaveModel,
	createCommonMediaObject,
	getOriginalImagesFromLayout
} from "client/Features/Hub/converters";
import { ArchivedContentStep } from "./ArchivedContentStep";
import { usePinnedArticlesDialog } from "../PinnedArticlesDialog/PinnedArticlesDialog";
import { KnowledgeItemPinModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { MAX_TAG_COUNT } from "client/Features/Hub/Builder/HubTagSelect";
import {
	createLayoutValidation,
	createMediaValidation
} from "client/Features/Hub/Builder/validation";
import {
	convertToLinkedDocumentFormData,
	convertToLinkedDocumentSaveModel,
	MAX_LINKED_DOCUMENTS_COUNT
} from "client/Features/Hub/Management/LinkedDocuments";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import { useHubComposerUnsavedChangesDialog } from "../../HubComposerUnsavedChangesDialog";
import {
	HubItemLayoutFormModel,
	HubItemMediaFormModel,
	tryFindHubCategoryItemNode
} from "../../common";
import { Preview } from "./Preview";
import { TimeZone } from "client/DateAndTime/timezones";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { never } from "client_legacy/ndaw/errors";
import { InfoAlertOptions, useInfoAlerts } from "../../HubItemComposerDialog/useInfoAlerts";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import { getLinkedDocumentId } from "client/Features/Hub/useAddDocuments";
import { LinkedDocumentFormData } from "client/Features/Hub/common";

enum FormStep {
	Content = 1,
	Settings = 2,
	WhoHasAccess = 3
}

export type KnowledgeItemFormModel = OverrideProperties<
	KnowledgeItemSaveModel,
	{
		linkedDocuments: readonly LinkedDocumentFormData[];
		media: HubItemMediaFormModel;
		layout: HubItemLayoutFormModel;
	}
>;

type FormModel = OverrideProperties<
	KnowledgeItemSaveRequestModel,
	{
		knowledgeItemSaveModel: KnowledgeItemFormModel;
	}
>;

export type EditKnowledgeItemDialogMode = {
	kind: "edit";
	itemId: number;
} | {
	kind: "new";
} | {
	kind: "new-from-template";
	templateId: number;
} | {
	kind: "duplicate";
	itemToCopyId: number;
};

export type EditDialogProps = {
	mode: EditKnowledgeItemDialogMode;
	onClose(wasAnyCategoryCreated: boolean): void;
	onSave?(): void;
	onBack?(wasAnyCategoryCreated: boolean): void;
	preferredCategoryId?: number | null;
};

export const EditDialog = (props: EditDialogProps): ReactElement => {
	const {
		mode: modeFromProps,
		onClose: onCloseFromProps,
		onSave,
		onBack,
		preferredCategoryId = null
	} = props;

	const mode: EditKnowledgeItemDialogMode = useValueEqualMemo(
		() => modeFromProps,
		[modeFromProps]
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isInPreviewMode, setIsInPreviewMode] = useState<boolean>(false);
	const [activeStep, setActiveStep] = useState<FormStep>(FormStep.Content);
	const [finishedSteps, setFinishedSteps] = useState<ReadonlySet<FormStep>>(new Set());
	const [hasServerError, setHasServerError] = useState<boolean>(false);
	const [wasAnyCategoryCreated, setWasAnyCategoryCreated] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormModel>({
		knowledgeItemSaveModel: {
			title: "",
			categoryId: null,
			isForEveryoneInTheCategory: true,
			media: {
				...createCommonMediaObject(),
				mediaKind: HubItemMediaKind.None
			},
			layout: { rows: [] },
			access: null,
			showAuthor: false,
			tags: [],
			linkedDocuments: [],
			isPinned: false,
			areCommentsEnabled: false
		},
		shouldPublishItem: false,
		shouldSendNotifications: null
	});

	const [initialFormData, setInitialFormData] = useState<FormModel>();
	const [state, setState] = useState<EditKnowledgeItemState>({ isLoaded: false });

	const [backdropState, setBackdropState] = useState<DialogBackdropState>();
	const [shouldFocusTitle, setShouldFocusTitle] = useState<boolean>(
		mode.kind === "new"
			? true
			: !isMobile
	);

	const {
		categoryHierarchy = EMPTY_ARRAY,
		imageRestrictions = {
			maxImageSize: 0,
			supportedFileExtensions: EMPTY_ARRAY,
			supportedMimeTypes: EMPTY_ARRAY
		},
		defaultImageLocation = null,
		originalImages = EMPTY_IMAGE_SET,
		usersAndUserGroupsData = EMPTY_USER_AND_USERGROUP_DATA,
		status = KnowledgeItemStatus.Draft,
		hasPermissionToCreateNewCategory = false,
		isPixabayImageGalleryAvailable = false
	} = state;

	const infoAlerts: ReactElement = useInfoAlerts(
		useMemo(
			(): readonly InfoAlertOptions[] => [
				{
					uniqueKey: "archived-message",
					message: localize("_ThisKnowledgeArticleCantBeEditedBecauseArchived"),
					shouldShow: state.status === KnowledgeItemStatus.Archived,
				},
				{
					uniqueKey: "duplicate-info",
					message: localize("_DuplicateHubItemInfoMessage"),
					shouldShow: mode.kind === "duplicate"
				}
			],
			[mode.kind, state.status]
		)
	);

	const contentStepRef: RefObject<ContentStepHandle | null> = useRef<ContentStepHandle>(null);
	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();
	const [leaveComposerDialogElement, openLeaveComposerDialog] = useHubComposerUnsavedChangesDialog();
	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const onClose: typeof onCloseFromProps = useStableImperativeCallback(onCloseFromProps);

	const [
		editCategoryDialog,
		openEditCategoryDialog
	] = useEditHubCategoryDialog();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormModel> => {
			const result: IValidationConfiguration<FormModel> = staticValidationConfiguration.clone();

			const saveModelValidation: IObjectValidatorBuilder<KnowledgeItemFormModel> = result.forObject("knowledgeItemSaveModel");

			saveModelValidation.forObject("media")
				.addValidators(
					createMediaValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			saveModelValidation.forObject("layout")
				.addValidators(
					createLayoutValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			return result;
		},
		[
			imageRestrictions.supportedMimeTypes,
			imageRestrictions.maxImageSize
		]
	);

	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);
	const [pinnedArticleDialogElement, openPinnedArticleDialog] = usePinnedArticlesDialog();

	const steps = useMemo(
		(): Step<FormStep>[] => {
			return initialSteps.map((x): Step<FormStep> => ({
				...x,
				isFinished: finishedSteps.has(x.value) || mode.kind === "edit",
				hasError: formHandler
					.forStep(formStepToValidationStep[x.value])
					.validationState
					.kind === InputValidationResultKind.Invalid
			}));
		},
		[
			mode.kind,
			finishedSteps,
			formHandler
		]
	);

	const previousStep = useMemo(
		(): FormStep | undefined => {
			const activeIndex: number = steps
				.findIndex(s => s.value === activeStep);

			return steps[activeIndex - 1]
				?.value;
		},
		[
			activeStep,
			steps
		]
	);

	const nextStep = useMemo(
		(): FormStep | undefined => {
			const activeIndex: number = steps
				.findIndex(s => s.value === activeStep);

			return steps[activeIndex + 1]
				?.value;
		},
		[
			activeStep,
			steps
		]
	);

	const isPublishAvailable = useMemo(
		(): boolean => {
			if (status === KnowledgeItemStatus.Archived) {
				return false;
			}

			const isFormValid: boolean = formHandler.validation
				.getValidationState()
				.kind === InputValidationResultKind.Valid;

			return steps
				.every(
					x => x.isFinished === true || x.value === activeStep
				)
					&& isFormValid;
		},
		[
			formHandler.validation,
			steps,
			activeStep,
			status
		]
	);

	const isSaveAsDraftAvailable = useMemo(
		(): boolean => {
			if (status !== KnowledgeItemStatus.Draft) {
				return false;
			}

			return Boolean(
				steps.find(
					x => x.value === FormStep.Settings
				)?.isFinished
			);
		},
		[status, steps]
	);

	const isDataChanged = useMemo(
		(): boolean => isNotNil(initialFormData)
			&& !deepComparison(formData, initialFormData),
		[
			formData,
			initialFormData
		]
	);

	useEffect(
		() => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					let model: EditKnowledgeItemPageViewModel;

					switch (mode.kind) {
						case "new":
							model = await getNewKnowledgeItemModel(
								undefined,
								undefined,
								signal
							);
							break;
						case "new-from-template":
							model = await getNewKnowledgeItemModel(
								undefined,
								mode.templateId,
								signal
							);
							break;
						case "duplicate":
							model = await getNewKnowledgeItemModel(
								mode.itemToCopyId,
								undefined,
								signal
							);
							break;
						case "edit":
							model = await getEditKnowledgeItemModel(mode.itemId, signal);
							break;
						default:
							never(mode);
					}

					const {
						knowledgeItem,
						usersAndUserGroupsData,
						knowledgeItemHierarchy,
						imageRestrictions,
						hasPermissionToCreateNewCategory,
						createdBy,
						publicationDate,
						defaultImageLocation,
						isPixabayImageGalleryAvailable
					} = model;

					let existingPreferredCategoryId: number | null = null;

					if (isNotNil(preferredCategoryId)) {
						const tryFindCategoryResult: TryResult<HubHierarchyListCategoryNodeModel> = tryFindHubCategoryItemNode(knowledgeItemHierarchy, preferredCategoryId);

						if (tryFindCategoryResult.wasSuccessful) {
							existingPreferredCategoryId = tryFindCategoryResult
								.result
								.category
								.id;
						}
					}

					setState({
						isLoaded: true,
						categoryHierarchy: knowledgeItemHierarchy,
						usersAndUserGroupsData,
						imageRestrictions,
						defaultImageLocation,
						hasPermissionToCreateNewCategory,
						status: knowledgeItem.status,
						originalImages: {
							coverImage: knowledgeItem.media.mediaKind === HubItemMediaKind.Image
								&& knowledgeItem.media.imageKind === HubImageMediaKind.BlobId
									? knowledgeItem.media
									: null,
							layoutImages: isNotNil(knowledgeItem.layout)
								? getOriginalImagesFromLayout(knowledgeItem.layout)
								: new Map()
						},
						originalViewModel: knowledgeItem,
						createdBy,
						publicationDate,
						isPixabayImageGalleryAvailable
					});

					const saveModel: FormModel = await convertToFormModel(
						knowledgeItem,
						existingPreferredCategoryId
					);

					setInitialFormData(saveModel);
					setFormData(saveModel);

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			onClose,
			preferredCategoryId,
			mode
		]
	);

	useEffect(
		() => {
			if (
				formHandler.validation
					.getValidationState()
					.kind === InputValidationResultKind.Invalid
						&& hasServerError
			) {
				for (const step of steps) {
					const hasError = formHandler.validation
						.getValidationStepStates(formStepToValidationStep[step.value])
						.some(x => x.kind === InputValidationResultKind.Invalid);

					if (hasError) {
						setActiveStep(step.value);
						setHasServerError(false);
						break;
					}
				}
			}
		},
		[
			formHandler.validation,
			hasServerError,
			steps
		]
	);

	useEffect(
		(): void => {
			if (activeStep !== FormStep.Content) {
				setShouldFocusTitle(false);
			}
		},
		[activeStep]
	);

	const onBeforeUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			const isChanged: boolean = isDataChanged
				|| (
					contentStepRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				beforeUnloadListener(event);
			}
		},
		[isDataChanged]
	);

	useEffect(
		() => {
			window
				.addEventListener(
					"beforeunload",
					onBeforeUnload
				);

			return () => window
				.removeEventListener(
					"beforeunload",
					onBeforeUnload
				);
		},
		[onBeforeUnload]
	);

	const newCategoryHandler = useCallback(
		async (): Promise<void> => {
			setBackdropState("invisible");

			const newCategoryResult: TryResult<HubCategorySavedResult> = await openEditCategoryDialog({
				kind: HubItemKind.KnowledgeItem,
				id: null
			});

			setBackdropState("visible");
			if (!newCategoryResult.wasSuccessful) {
				return;
			}

			const {
				id,
				name
			} = newCategoryResult.result;

			setState(
				state => {
					if (!state.isLoaded) {
						console.error("Cannot add a new category to not loaded state.");
						return state;
					}

					const newCategoryNode: HubHierarchyListCategoryNodeModel = {
						kind: HubItemCategoryNodeKind.Category,
						category: {
							id,
							name,
							canCreateItem: true,
							isFollowing: false,
							hubItemKind: HubItemKind.KnowledgeItem,
							kind: HubItemCategoryKind.Custom
						}
					};

					return {
						...state,
						categoryHierarchy: [
							...state.categoryHierarchy,
							newCategoryNode
						]
					};
				}
			);

			setWasAnyCategoryCreated(true);

			setFormData(
				formData => ({
					...formData,
					knowledgeItemSaveModel: {
						...formData.knowledgeItemSaveModel,
						categoryId: id
					}
				})
			);

			formHandler
				.validation
				.for("knowledgeItemSaveModel")
				.for("categoryId")
				.triggerInputFinished();
		},
		[
			formHandler.validation,
			openEditCategoryDialog
		]
	);

	const attachable: IObjectInputAttachable<KnowledgeItemFormModel> = formHandler
		.for("knowledgeItemSaveModel");

	const showPinnedArticlesDialog = useCallback(
		async (results: KnowledgeItemPinModel[], categoryName: string ): Promise<TryResult<void>> => {

			const result: TryResult<void> = await openPinnedArticleDialog({
				pinnedItems: results,
				categoryName
			});

			return result;

		}
		,[openPinnedArticleDialog]
	);

	const ensureItemCanBePinnedInCategory = useCallback(
		async (): Promise<boolean> => {
			const {
				categoryId,
				isPinned
			} = formData.knowledgeItemSaveModel;

			if(isNotNil(categoryId) && isPinned) {
				const id: number = mode.kind === "edit"
						? mode.itemId
						: 0;

				let existingPreferredCategoryName: string = "";

				const tryFindCategoryResult: TryResult<HubHierarchyListCategoryNodeModel> = tryFindHubCategoryItemNode(
					categoryHierarchy,
					categoryId
				);

				if (tryFindCategoryResult.wasSuccessful) {
					existingPreferredCategoryName = tryFindCategoryResult
						.result
						.category
						.name;
				}

				const results: CheckPinLimitResult = await checkPinLimit(categoryId, id);
				if(!results.canBePinned) {
					const result: TryResult<void> = await showPinnedArticlesDialog(results.pinnedItems, existingPreferredCategoryName);

					if(!result.wasSuccessful) {
						setActiveStep(FormStep.Settings);
						attachable.for("isPinned").reducer.setValue(false);
						return false;
					}

				}
			}
			return true;
		},
		[
			formData.knowledgeItemSaveModel,
			mode,
			categoryHierarchy,
			showPinnedArticlesDialog,
			attachable
		]
	);

	const publishHandler = useCallback(
		async (): Promise<void> => {
			if (status === KnowledgeItemStatus.Archived) {
				return;
			}

			const confirmationDialogResult: TryResult<void> = await openConfirmationDialog({
				trackingId: "send-an-activity-about-this-item",
				title: localize("_DoYouWantToSendAnActivityAboutThisItem?"),
				confirmText: localize("_YesSendAnActivity"),
				cancelText: localize("_NoDontSend")
			});

			const shouldSendNotifications: boolean = confirmationDialogResult
				.wasSuccessful;

			setIsBusy(true);
			try {
				const result: InputValidationResult = await formHandler
					.validation
					.validate();

				const isPinCheckSuccessful = await ensureItemCanBePinnedInCategory();

				if (result.kind !== InputValidationResultKind.Valid || !isPinCheckSuccessful) {
					setIsBusy(false);
					setIsInPreviewMode(false);
					return;
				}

				const saveModel: KnowledgeItemSaveRequestModel = {
					knowledgeItemSaveModel: prepareKnowledgeItem(formData.knowledgeItemSaveModel),
					shouldPublishItem: true,
					shouldSendNotifications
				};

				switch (mode.kind) {
					case "new":
					case "new-from-template":
					case "duplicate":
						await createKnowledgeItem(saveModel);
						break;
					case "edit":
						await updateKnowledgeItem(
							mode.itemId,
							saveModel
						);
						break;
					default:
						never(mode);
				}

				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullySubmittedTheKnowledgeArticle"));
				onSave?.();
				onClose(wasAnyCategoryCreated);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				const wasHandled: boolean = formHandler
					.validation
					.tryProcessServerError(e);

				if (wasHandled) {
					setIsInPreviewMode(false);
				} else {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
				setHasServerError(true);
			}
		},
		[
			status,
			openConfirmationDialog,
			formHandler.validation,
			ensureItemCanBePinnedInCategory,
			formData.knowledgeItemSaveModel,
			mode,
			alertManager,
			onSave,
			onClose,
			wasAnyCategoryCreated
		]
	);

	const saveDraftHandler = useCallback(
		async (): Promise<void> => {
			if (status === KnowledgeItemStatus.Archived) {
				return;
			}

			setIsBusy(true);
			try {
				const result: InputValidationResult = await formHandler
					.validation
					.validate();

				if (result.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					setIsInPreviewMode(false);
					return;
				}

				switch (mode.kind) {
					case "new":
					case "new-from-template":
					case "duplicate":
						await createKnowledgeItem({
							knowledgeItemSaveModel: prepareKnowledgeItem(formData.knowledgeItemSaveModel),
							shouldPublishItem: false,
							shouldSendNotifications: null
						});
						break;
					case "edit":
						await updateKnowledgeItem(
							mode.itemId,
							{
								knowledgeItemSaveModel: prepareKnowledgeItem(formData.knowledgeItemSaveModel),
								shouldPublishItem: false,
								shouldSendNotifications: null
							}
						);
						break;
					default:
						never(mode);
				}

				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullyCreatedADraft"));
				onSave?.();
				onClose(wasAnyCategoryCreated);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				const wasHandled: boolean = formHandler
					.validation
					.tryProcessServerError(e);

				if (wasHandled) {
					setIsInPreviewMode(false);
				} else {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
				setHasServerError(true);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			mode,
			wasAnyCategoryCreated,
			onClose,
			onSave,
			status
		]
	);

	const goToPreviousStep = useCallback(
		(): void => {
			if (isNil(previousStep)) {
				return;
			}
			setActiveStep(previousStep);
		},
		[
			previousStep
		]
	);

	const goToNextStep = useCallback(
		async (): Promise<void> => {
			if (isNil(nextStep)) {
				return;
			}

			const stepValidationResult: InputValidationResult = await formHandler
				.validation
				.validateStep(formStepToValidationStep[activeStep]);

			if (stepValidationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			if(activeStep === FormStep.Settings) {
				const result: boolean = await ensureItemCanBePinnedInCategory();

				if(!result) {
					return;
				}
			}

			setFinishedSteps(x => new Set([...x, activeStep, nextStep]));
			setActiveStep(nextStep);
		},
		[
			nextStep,
			formHandler.validation,
			activeStep,
			ensureItemCanBePinnedInCategory
		]
	);

	const activeStepChangeHandler = useCallback(
		(value: FormStep): void => {
			setFinishedSteps(x => new Set([...x, activeStep]));
			setActiveStep(value);
		},
		[activeStep]
	);

	const leaveWithConfirmationCheck = useCallback(
		async (leaveAction: () => void): Promise<void> => {
			const isChanged: boolean = isDataChanged
				|| (
					contentStepRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				const confirmation: TryResult<boolean> = await openLeaveComposerDialog({
					shouldShowSaveAsDraft: isSaveAsDraftAvailable
				});

				if (confirmation.wasSuccessful) {
					if (confirmation.result) {
						await saveDraftHandler();
					} else {
						leaveAction();
					}
				}
			} else {
				leaveAction();
			}
		},
		[
			isDataChanged,
			isSaveAsDraftAvailable,
			openLeaveComposerDialog,
			saveDraftHandler
		]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			return onBack
				? () => leaveWithConfirmationCheck(
					() => onBack(wasAnyCategoryCreated)
				)
				: undefined;
		},
		[
			leaveWithConfirmationCheck,
			onBack,
			wasAnyCategoryCreated
		]
	);

	const closeHandler = useCallback(
		(): void => {
			leaveWithConfirmationCheck(
				() => onClose(wasAnyCategoryCreated)
			);
		},
		[
			leaveWithConfirmationCheck,
			onClose,
			wasAnyCategoryCreated
		]
	);

	const renderFooter = (): ReactElement => (
		<Toolbar>
			{!isMobile && (
				<>
					<Toolbar.Toggle
						value={isInPreviewMode}
						onChange={setIsInPreviewMode}
						text={localize("_Preview")}
						trackingId="preview-mode"
						shouldAutoCloseResponsiveDropOver
					/>
					<Toolbar.SpaceBetween />
				</>
			)}
			<Toolbar.Button
				trackingId="previous"
				kind="secondary"
				text={localize("_Previous")}
				isDisabled={isInPreviewMode || isNil(previousStep)}
				onClick={goToPreviousStep}
			/>
			{state.status === KnowledgeItemStatus.Draft && (
				<Toolbar.Button
					trackingId="save-draft"
					kind="secondary"
					text={localize("_SaveAsDraft")}
					onClick={saveDraftHandler}
					isDisabled={!isSaveAsDraftAvailable}
				/>
			)}
			<Toolbar.Button
				trackingId="next"
				kind="primary"
				text={localize("_Next")}
				isDisabled={isInPreviewMode || isNil(nextStep)}
				onClick={goToNextStep}
			/>
			<Toolbar.Button
				trackingId="publish"
				kind="primary"
				text={localize("_Publish")}
				isDisabled={!isPublishAvailable}
				onClick={publishHandler}
			/>
		</Toolbar>
	);

	const renderPreview = (): ReactElement | null => {
		if (!isInPreviewMode
			|| !state.isLoaded
			|| isMobile
		) {
			return null;
		}

		return (
			<Preview
				model={formData.knowledgeItemSaveModel}
				timezone={userTimezone}
				categoryHierarchy={state.categoryHierarchy}
				originalImages={state.originalImages}
				createdBy={state.createdBy}
				publicationDate={state.publicationDate}
			/>
		);
	};

	return (
		<>
			<HubItemComposerDialog
				title={
					mode.kind === "new" || mode.kind === "duplicate"
						? localize("_NewKnowledgeArticle")
						: localize("_EditKnowledgeArticle")
				}
				onClose={closeHandler}
				onBack={backHandler}
				activeStep={activeStep}
				isBusy={isBusy}
				steps={steps}
				footer={renderFooter()}
				preview={renderPreview()}
				goToNextStep={goToNextStep}
				onActiveStepChanged={activeStepChangeHandler}
				trackingId="knowledge-item-composer-dialog"
				backdrop={backdropState}
			>
				{status === KnowledgeItemStatus.Archived && state.isLoaded
					? (
						<ArchivedContentStep
							isActive={activeStep === FormStep.Content}
							model={formData.knowledgeItemSaveModel}
							onChangeIsLoading={setIsBusy}
							originalImages={state.originalImages}
							infoAlerts={infoAlerts}
						/>
					)
					: (
						<ContentStep
							ref={contentStepRef}
							isActive={activeStep === FormStep.Content}
							attachable={attachable}
							imageRestrictions={imageRestrictions}
							defaultImageLocation={defaultImageLocation}
							originalImages={originalImages}
							onChangeIsLoading={setIsBusy}
							shouldFocusTitle={shouldFocusTitle}
							infoAlerts={infoAlerts}
							isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
						/>
					)
				}
				<SettingsStep
					isActive={activeStep === FormStep.Settings}
					attachable={attachable}
					status={status}
					categoryHierarchy={categoryHierarchy}
					hasPermissionToCreateNewCategory={hasPermissionToCreateNewCategory}
					onCreateNewCategory={newCategoryHandler}
					infoAlerts={infoAlerts}
				/>
				<WhoHasAccessStep
					isActive={activeStep === FormStep.WhoHasAccess}
					attachable={attachable}
					status={status}
					usersAndUserGroupsData={usersAndUserGroupsData}
					infoAlerts={infoAlerts}
				/>
			</HubItemComposerDialog>
			{editCategoryDialog}
			{confirmationDialogElement}
			{leaveComposerDialogElement}
			{pinnedArticleDialogElement}
		</>
	);
};
EditDialog.displayName="EditDialog";

const staticValidationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();
const knowledgeItemValidation: IObjectValidatorBuilder<KnowledgeItemFormModel> = staticValidationConfiguration
	.forObject("knowledgeItemSaveModel");

knowledgeItemValidation
	.forScalar("title")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

knowledgeItemValidation
	.forScalar("categoryId")
	.add(
		isNotNullNorUndefined()
	);

knowledgeItemValidation
	.forCollection("tags")
	.add(
		hasMaximumEntries(MAX_TAG_COUNT)
	);

knowledgeItemValidation
	.forCollection("linkedDocuments")
	.add(
		hasMaximumEntries(MAX_LINKED_DOCUMENTS_COUNT)
	);

knowledgeItemValidation
	.forCollection("linkedDocuments")
	.forItems(getLinkedDocumentId)
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const contentValidationStep: IObjectValidationStepBuilder<KnowledgeItemSaveModel> = configureValidationStep<FormModel>()
	.for("knowledgeItemSaveModel")
	.add(
		"title",
		"layout",
		"media",
		"linkedDocuments"
	);

const settingsValidationStep: IObjectValidationStepBuilder<KnowledgeItemSaveModel> = configureValidationStep<FormModel>()
	.for("knowledgeItemSaveModel")
	.add(
		"categoryId",
		"showAuthor",
		"isPinned",
		"tags"
	);

const whoHasAccessValidationStep: IObjectValidationStepBuilder<KnowledgeItemSaveModel> = configureValidationStep<FormModel>()
	.for("knowledgeItemSaveModel")
	.add(
		"isForEveryoneInTheCategory",
		"access"
	);

const formStepToValidationStep: { [key in FormStep]: IObjectValidationStepBuilder<KnowledgeItemSaveModel> } = {
	[FormStep.Content]: contentValidationStep,
	[FormStep.Settings]: settingsValidationStep,
	[FormStep.WhoHasAccess]: whoHasAccessValidationStep,
};

type EditKnowledgeItemState = ConditionalProps<
	{
		categoryHierarchy: readonly HubHierarchyListNodeModel[];
		originalViewModel: KnowledgeItemViewModel;
		originalImages: ExistingHubItemImages;
		hasPermissionToCreateNewCategory: boolean;
		usersAndUserGroupsData: UsersAndUserGroupsDataModel;
		imageRestrictions: HubItemImageRestrictionsModel;
		defaultImageLocation: DocumentLocationViewModel | null;
		status: KnowledgeItemStatus;
		createdBy: UserIdentityModel | null;
		publicationDate: Date | string | null;
		isPixabayImageGalleryAvailable: boolean;
	},
	"isLoaded"
>;

const initialSteps: readonly Step<FormStep>[] = [
	{
		title: localize("_Content"),
		value: FormStep.Content,
		valueTrackingId: "content-step"
	},
	{
		title: localize("_Settings"),
		value: FormStep.Settings,
		valueTrackingId: "settings-step"
	},
	{
		title: localize("_WhoHasAccess"),
		value: FormStep.WhoHasAccess,
		valueTrackingId: "who-has-access-step"
	}
];

export type EditKnowledgeItemDialogInitialData = Omit<EditDialogProps, "onClose" | "onSave">;

export function useEditKnowledgeItemDialog(): [
	element: ReactElement | null,
	open: (props: EditKnowledgeItemDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditDialog
				{...props}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

export function openKnowledgeItemEditDialog(props: Omit<EditDialogProps, "onClose">): void {
	renderReactDialog(
		onClose => (
			<EditDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}

async function convertToFormModel(
	viewModel: KnowledgeItemViewModel,
	preferredCategoryId: number | null
): Promise<FormModel> {
	const {
		title,
		media,
		categoryId,
		isForEveryoneInTheCategory,
		showAuthor,
		access,
		tags,
		linkedDocuments,
		isPinned,
		areCommentsEnabled
	} = viewModel;

	return {
		knowledgeItemSaveModel: {
			title: title ?? "",
			layout: isNotNil(viewModel.layout)
				? await convertLayoutToSaveModel(viewModel.layout)
				: { rows: [] },
			media: await convertMediaToSaveModel(media),
			categoryId: categoryId ?? preferredCategoryId,
			isForEveryoneInTheCategory,
			showAuthor,
			access,
			tags: tags ?? [],
			isPinned,
			linkedDocuments: linkedDocuments
				?.map(convertToLinkedDocumentFormData) ?? [],
			areCommentsEnabled
		},
		shouldPublishItem: false,
		shouldSendNotifications: null
	};
}

function prepareKnowledgeItem(model: KnowledgeItemFormModel): KnowledgeItemSaveModel {
	return {
		...model,
		linkedDocuments: model.linkedDocuments
			.map(convertToLinkedDocumentSaveModel),
		media: cleanUpMedia(model.media)
	};
}

function beforeUnloadListener(event: BeforeUnloadEvent): string {
	event.preventDefault();
	event.returnValue = localize("_YouHaveUnsavedChanges");
	return event.returnValue;
};

const EMPTY_IMAGE_SET: ExistingHubItemImages = {
	coverImage: null,
	layoutImages: new Map()
};

const EMPTY_USER_AND_USERGROUP_DATA: UsersAndUserGroupsDataModel = {
	users: [],
	userGroups: []
};
